import { isPresent } from '@luminovo/commons';
import { SearchIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { AppLinkOptions, asLinkProps } from '../../routing/links/linkOptions';
import { colorSystem } from '../../theme';
import { BaseButtonProps, SecondaryButton } from '../buttons';
import { Flexbox } from '../Flexbox';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';
import { Text } from '../Text';

/** A small secondary button; with `link`, built with `linkOptions()`, it is a link and takes no `onClick`. */
export type NonIdealStateAction = Pick<BaseButtonProps, 'startIcon' | 'disabled' | 'isLoading' | 'size'> & {
    children: React.ReactNode;
} & ({ onClick: NonNullable<BaseButtonProps['onClick']>; link?: never } | { link: AppLinkOptions; onClick?: never });

export function NonIdealState({
    title,
    description,
    action,
    withBorder = false,
    icon = <SearchIcon />,
    variant = 'default',
    style,
}: {
    /** The button below the text; pass an element instead for another button variant or several buttons. */
    // `<unknown>` matches what `isValidElement` narrows to, so the else branch is a `NonIdealStateAction`.
    action?: NonIdealStateAction | React.ReactElement<unknown>;
    title: React.ReactNode;
    description?: React.ReactNode;
    withBorder?: boolean;
    /** Drawn at 32px in the variant's color. `null` shows no icon. */
    icon?: IconElement;
    variant?: 'default' | 'error' | 'warning' | 'success';
    style?: React.CSSProperties;
}): JSX.Element {
    const variants = {
        default: {
            iconColor: colorSystem.neutral[8],
            titleColor: colorSystem.neutral[9],
        },
        error: {
            iconColor: colorSystem.red[7],
            titleColor: colorSystem.red[8],
        },
        warning: {
            iconColor: colorSystem.yellow[7],
            titleColor: colorSystem.yellow[8],
        },
        success: {
            iconColor: colorSystem.green[7],
            titleColor: colorSystem.green[8],
        },
    };

    const selectedVariant = variants[variant];

    return (
        <Flexbox
            gap={8}
            style={style}
            sx={{
                textAlign: 'center',
                flexDirection: 'column',
                maxWidth: '400px',
                alignItems: 'center',
                border: withBorder ? `1px solid ${colorSystem.neutral[2]}` : undefined,
                bgcolor: withBorder ? colorSystem.neutral.white : undefined,
                padding: '60px 40px',
                borderRadius: '8px',
                // Sizes and colors the icon: it gets neither.
                ...iconSlot(32),
                '& > svg': { color: selectedVariant.iconColor },
            }}
        >
            {icon}
            <Flexbox
                gap={'4px'}
                sx={{
                    flexDirection: 'column',
                    // With the 8px gap below, the action sits 16px under the text.
                    marginBottom: isPresent(action) ? '8px' : undefined,
                }}
            >
                <Text role="heading" aria-level={2} color={selectedVariant.titleColor} variant="h4">
                    {title}
                </Text>
                {isPresent(description) && (
                    <Text
                        color={colorSystem.neutral[8]}
                        style={{
                            textWrap: 'balance',
                        }}
                    >
                        {description}
                    </Text>
                )}
            </Flexbox>
            {React.isValidElement(action) ? action : isPresent(action) && <DefaultActionButton action={action} />}
        </Flexbox>
    );
}

function DefaultActionButton({ action }: { action: NonIdealStateAction }): JSX.Element {
    const { children, startIcon, disabled, isLoading, size = 'small' } = action;
    const buttonProps = { children, startIcon, disabled, isLoading, size };
    return action.link === undefined ? (
        <SecondaryButton {...buttonProps} onClick={action.onClick} />
    ) : (
        <SecondaryButton {...buttonProps} {...asLinkProps(action.link)} />
    );
}
